import { useState, useEffect, useCallback, useRef } from 'react'
import { api, reportApi } from '../api'
import { Pill } from './Pill'
import { ErrorState, errorMessage } from './ErrorState'
import { TriageControls } from './TriageControls'
import { enumLabel, translate, useI18n } from '../i18n'
import type { TFunction } from '../i18n'
import { scoreColor } from '../types'
import type { UnauthorizedMerchantDto, PaginatedResponse, WebScanCoverage } from '../types'

/**
 * WebScanPanel — onglet N4 du dashboard
 *
 * Affiche une carte du monde (D3 + TopoJSON) avec les pays colorés
 * selon le nombre de marchands non déclarés trouvés.
 *
 * Clique sur un pays → filtre la liste à droite.
 *
 * Dépendances CDN (chargées dynamiquement) :
 *  - d3 v7
 *  - topojson v3
 *  - world-atlas countries-110m.json
 */

/** Pays couverts par la rotation N4 cote backend (COUNTRY_CONFIGS). */
const MONITORED_COUNTRY_COUNT = 13

/** Pays proposés au lancement d'un scan ciblé. Le nom vient du catalogue. */
const SCAN_COUNTRY_CODES = ['SN', 'CI', 'ML', 'BF', 'CM']

function scanCountryLabel(t: TFunction, code: string): string {
  return code ? enumLabel(t, 'country', code, code) : t('common.allCountries')
}

export function WebScanPanel() {
  const { t, fmt } = useI18n()
  const [data, setData]           = useState<PaginatedResponse<UnauthorizedMerchantDto> | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [filterBrand, setFilterBrand]         = useState('')
  const [filterAction, setFilterAction]       = useState('')
  const [page, setPage]           = useState(1)
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState<string | null>(null)
  const [coverage, setCoverage]   = useState<WebScanCoverage | null>(null)
  const [countsByCountry, setCountsByCountry] = useState<Record<string, number>>({})
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInitialized = useRef(false)
  const [scanRunning, setScanRunning] = useState(false)
  const [scanCountry, setScanCountry] = useState('')
  const [scanResult, setScanResult] = useState<{ message: string; isError: boolean } | null>(null)
  const scanPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.getWebScanResults({
        country: selectedCountry || undefined,
        brand: filterBrand || undefined,
        action: filterAction || undefined,
        page,
        limit: 15,
      })
      setData(res)

      // Calculer les comptes par pays pour la carte
      const counts = await api.getWebScanCountsByCountry()
      setCountsByCountry(counts)
      setError(null)
    } catch (err: unknown) {
      setData(null)
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [selectedCountry, filterBrand, filterAction, page])

  useEffect(() => { fetchData() }, [fetchData])
  useEffect(() => { setPage(1) }, [selectedCountry, filterBrand, filterAction])

  // La couverture ne dépend d'aucun filtre : une seule lecture au montage.
  useEffect(() => {
    api.getWebScanCoverage().then(setCoverage).catch(() => setCoverage(null))
  }, [])

  const syncWebScanStatus = useCallback(async () => {
    try {
      const status = await api.getScanStatus()
      setScanRunning(status.webscan)
      return status.webscan
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    syncWebScanStatus()
  }, [syncWebScanStatus])

  useEffect(() => {
    if (scanRunning && !scanPollRef.current) {
      scanPollRef.current = setInterval(async () => {
        const running = await syncWebScanStatus()
        if (!running) {
          if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null }
          fetchData()
        }
      }, 3000)
    }
    if (!scanRunning && scanPollRef.current) {
      clearInterval(scanPollRef.current)
      scanPollRef.current = null
    }
    return () => { if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null } }
  }, [scanRunning, syncWebScanStatus])

  const triggerWebScan = useCallback(async () => {
    if (scanRunning) return
    setScanRunning(true)
    setScanResult(null)
    try {
      const res = await api.triggerScanWeb(scanCountry || undefined)
      const total = res.totalResults ?? res.total ?? 0
      const label = scanCountry ? enumLabel(t, 'country', scanCountry, scanCountry) : t('webscan.allCountriesLabel')
      setScanResult({ message: t('webscan.scanDone', { total, label }), isError: false })
      fetchData()
    } catch (err) {
      setScanResult({ message: t('webscan.scanError', { error: errorMessage(err) }), isError: true })
    } finally {
      await syncWebScanStatus()
    }
  }, [scanRunning, scanCountry, fetchData, syncWebScanStatus, t])

  useEffect(() => {
    if (!scanResult) return
    const t = setTimeout(() => setScanResult(null), 15_000)
    return () => clearTimeout(t)
  }, [scanResult])

  // Map rendering via D3 (loaded once)
  useEffect(() => {
    if (mapInitialized.current || !mapRef.current) return
    mapInitialized.current = true
    initWorldMap(mapRef.current, countsByCountry, (code) => {
      setSelectedCountry((prev) => prev === code ? null : code)
    })
  }, [])

  // Update map colors when data changes
  useEffect(() => {
    updateMapColors(countsByCountry, selectedCountry)
  }, [countsByCountry, selectedCountry])

  const totalFound = Object.values(countsByCountry).reduce((a, b) => a + b, 0)
  const countriesWithResults = Object.keys(countsByCountry).length

  return (
    <div>
      {/* Scan toolbar */}
      <div className="scan-toolbar">
        <span className="scan-toolbar-label">{t('webscan.runScan')}</span>
        <select
          className="scan-country-select"
          value={scanCountry}
          onChange={e => setScanCountry(e.target.value)}
          disabled={scanRunning}
        >
          {['', ...SCAN_COUNTRY_CODES].map(code => (
            <option key={code} value={code}>{scanCountryLabel(t, code)}</option>
          ))}
        </select>
        <button
          className={`scan-btn ${scanRunning ? 'scan-btn-active' : ''}`}
          disabled={scanRunning}
          onClick={triggerWebScan}
        >
          {scanRunning ? (
            <><span className="scan-spinner" /> {t('webscan.scanRunning')}</>
          ) : (
            <>{'\u{1F310}'} {t('webscan.scanBtn')}</>
          )}
        </button>
      </div>

      {scanResult && (
        <div className={`scan-result ${scanResult.isError ? 'scan-result-error' : 'scan-result-ok'}`}>
          {scanResult.message}
        </div>
      )}

      {/* Le scan ne sait pas distinguer autorisé / non autorisé sans contrats
          enregistrés : le dire franchement plutôt que laisser lire ces lignes
          comme des infractions constatées. */}
      {coverage?.warning && (
        <div className={coverage.canDiscriminate ? 'coverage-notice' : 'coverage-notice coverage-notice-blocking'} role="status">
          <span className="coverage-notice-icon" aria-hidden="true">!</span>
          <div>
            <p className="coverage-notice-title">
              {coverage.canDiscriminate
                ? t('webscan.coveragePartial')
                : t('webscan.coverageInactive')}
            </p>
            <p className="coverage-notice-msg">{coverage.warning}</p>
          </div>
        </div>
      )}

      {/* Metrics — uniquement des valeurs issues de l'API. */}
      <div className="stats-grid" style={{ marginBottom: '16px' }}>
        <div className="stat-card">
          <div className="stat-label">{t('webscan.foundLabel')}</div>
          <div className="stat-value value-danger">{fmt.number(totalFound)}</div>
          <div className="stat-sub stat-sub-neutral">{t('webscan.foundSub')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('webscan.countriesLabel')}</div>
          <div className="stat-value">{fmt.number(countriesWithResults)}</div>
          <div className="stat-sub stat-sub-neutral">{t('webscan.countriesSub', { n: MONITORED_COUNTRY_COUNT })}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('webscan.shownLabel')}</div>
          <div className="stat-value">{fmt.number(data?.data.length ?? 0)}</div>
          <div className="stat-sub stat-sub-neutral">
            {data ? t('common.page', { page: data.page, pages: data.totalPages || 1 }) : '—'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('webscan.scanStateLabel')}</div>
          <div className="stat-value" style={{ fontSize: '18px' }}>
            {scanRunning ? t('common.yes') : t('common.no')}
          </div>
          <div className="stat-sub stat-sub-neutral">
            {scanRunning ? t('webscan.scanActive') : t('webscan.scanIdle')}
          </div>
        </div>
      </div>

      {/* Map + List */}
      <div className="webscan-grid">
        {/* World Map */}
        <div className="chart-card">
          <div className="chart-title">{t('webscan.mapTitle')}</div>
          <div className="chart-legend">
            {/* Ordre décroissant : la légende se lit du plus grave au moins grave,
                comme l'échelle de la carte. */}
            <span className="legend-item"><span className="legend-dot" style={{ background: '#991B1B' }} />{t('webscan.legend6plus')}</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#DC2626' }} />{t('webscan.legend3to5')}</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#FCA5A5' }} />{t('webscan.legend1to2')}</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#E2E8F0' }} />{t('webscan.legendNone')}</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#F1F5F9' }} />{t('webscan.legendOutside')}</span>
          </div>
          <div ref={mapRef} id="n4-world-map" style={{ width: '100%', minHeight: '300px' }} />
          {selectedCountry && (
            <button onClick={() => setSelectedCountry(null)} style={{ marginTop: '8px', fontSize: '12px' }}>
              {t('webscan.resetCountry')}
            </button>
          )}
        </div>

        {/* Results list */}
        <div className="table-card" style={{ maxHeight: '420px', overflowY: 'auto' }}>
          <div className="table-header">
            <h2 style={{ fontSize: '13px' }}>
              {selectedCountry
                ? t('webscan.resultsFor', { country: enumLabel(t, 'country', selectedCountry, selectedCountry) })
                : t('webscan.allResults')}
              <button
                type="button"
                className="triage-link"
                style={{ marginLeft: 10, fontWeight: 400 }}
                onClick={() => reportApi.downloadDetectionsCsv('webscan').catch(() => undefined)}
                title={t('webscan.exportCsvTitle')}
              >
                {t('webscan.exportCsv')}
              </button>
            </h2>
            <div className="filter-row">
              <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}>
                <option value="">{t('webscan.allBrands')}</option>
                <option value="InTouch">InTouch</option>
                <option value="Wave">Wave</option>
                <option value="Orange Money">Orange Money</option>
              </select>
              <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
                <option value="">{t('webscan.allActions')}</option>
                <option value="BLOCK">{t('webscan.action.BLOCK')}</option>
                <option value="INVESTIGATE">{t('webscan.action.INVESTIGATE')}</option>
                <option value="ONBOARD">{t('webscan.action.ONBOARD')}</option>
              </select>
            </div>
          </div>

          {data?.data.map((m) => (
            <div key={m.id} className="webscan-result-item">
              <div className="webscan-result-top">
                <div>
                  <div className="merchant-name">{m.title}</div>
                  <div className="merchant-meta">
                    {/* Le lien s'ouvre dans un nouvel onglet : on ne quitte pas la liste en cours
                        d'examen, et le site visé n'a pas accès à cette page. */}
                    <a
                      className="webscan-url"
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      title={t('webscan.openInNewTab', { url: m.url })}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {m.url}
                    </a>{' '}
                    — {m.country ? enumLabel(t, 'country', m.country, m.country) : t('webscan.worldwide')}{' '}
                    — {m.detectedCategory ?? t('webscan.uncategorised')}
                  </div>
                  <div className="webscan-result-tags">
                    {m.paymentMethodsFound.map((p) => (
                      <span key={p} className="keyword-tag">{p}</span>
                    ))}
                  </div>
                  {/* Le contraste qui fait tout l'intérêt du N4 : marchand
                      déclaré, mais pas pour la marque qui l'a trouvé. */}
                  {(m.matchType ?? m.matchResult?.type) === 'KNOWN_UNAUTHORIZED' && (
                    <div className="webscan-unauthorized-hint">
                      <strong>{m.matchedMerchantName ?? t('webscan.knownMerchant')}</strong> — {t('webscan.authorizedFor')}{' '}
                      {m.authorizedBrands?.length
                        ? m.authorizedBrands.join(', ')
                        : t('webscan.noBrandRegistered')}{' '}
                      — <strong>{t('webscan.butNotFor', { brand: m.searchedBrand })}</strong>
                    </div>
                  )}
                </div>
                <div className="webscan-result-right">
                  <Pill
                    label={enumLabel(t, 'webscan.action', m.action, m.action)}
                    title={m.action}
                    variant={m.action === 'BLOCK' ? 'critical' : m.action === 'INVESTIGATE' ? 'warning' : 'ok'}
                  />
                  <span className="webscan-result-score" style={{ color: scoreColor(m.riskScore) }}>
                    {t('webscan.scoreLabel', { score: m.riskScore })}
                  </span>
                </div>
              </div>

              <TriageControls
                kind="webscan"
                id={m.id}
                status={m.triageStatus}
                assignedTo={m.assignedTo}
                onChanged={fetchData}
              />
            </div>
          ))}

          {error && <ErrorState message={error} onRetry={fetchData} compact />}

          {!error && data && data.data.length === 0 && (
            <div className="table-empty">{t('webscan.emptyFiltered')}</div>
          )}

          {!error && data && data.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1 || loading}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('webscan.paginationInfo', { page: data.page, pages: data.totalPages, total: data.total })}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={data.page >= data.totalPages || loading}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── D3 World Map (loaded via CDN in index.html) ──────────────────────────────
// Ces fonctions utilisent les globaux window.d3 et window.topojson
// chargés via <script src="..."> dans le HTML.

function initWorldMap(
  container: HTMLElement,
  counts: Record<string, number>,
  onCountryClick: (code: string) => void,
) {
  const d3 = (window as any).d3
  const topojson = (window as any).topojson
  if (!d3 || !topojson) {
    // `translate` et non `useT` : on est hors rendu React, dans le code de carte.
    container.textContent = translate('webscan.mapUnavailable')
    return
  }

  const isDark = document.documentElement.dataset.theme === 'dark'

  const svg = d3.select(container).append('svg')
    .attr('viewBox', '0 0 960 500')
    .attr('width', '100%')

  const projection = d3.geoNaturalEarth1().scale(155).translate([480, 260])
  const path = d3.geoPath(projection)

  d3.json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json').then((world: any) => {
    const countries = topojson.feature(world, world.objects.countries).features

    svg.selectAll('path').data(countries).join('path')
      .attr('class', 'n4-country')
      .attr('d', path)
      .attr('stroke', isDark ? 'rgba(255,255,255,.1)' : '#fff')
      .attr('stroke-width', 0.5)
      .attr('fill', (d: any) => getCountryFill(d.id, counts, isDark))
      .attr('cursor', (d: any) => isScannedCountry(d.id) ? 'pointer' : 'default')
      .on('click', (ev: any, d: any) => {
        const code = numericToIso(d.id)
        if (code) onCountryClick(code)
      })
  })
}

function updateMapColors(counts: Record<string, number>, selected: string | null) {
  const d3 = (window as any).d3
  if (!d3) return
  const isDark = document.documentElement.dataset.theme === 'dark'

  d3.selectAll('.n4-country')
    .attr('fill', (d: any) => getCountryFill(d.id, counts, isDark))
    .attr('stroke-width', (d: any) => {
      const code = numericToIso(d.id)
      return selected && code === selected ? 2 : 0.5
    })
    .attr('stroke', (d: any) => {
      const code = numericToIso(d.id)
      if (selected && code === selected) return isDark ? '#85B7EB' : '#185FA5'
      return isDark ? 'rgba(255,255,255,.1)' : '#fff'
    })
}

// ─── Mapping ISO numérique → ISO alpha-2 ─────────────────────────────────────

const NUMERIC_TO_ISO: Record<number, string> = {
  686: 'SN', 384: 'CI', 466: 'ML', 854: 'BF',
  324: 'GN', 768: 'TG', 204: 'BJ', 562: 'NE',
  120: 'CM', 288: 'GH', 250: 'FR', 56: 'BE',
  124: 'CA', 826: 'GB', 276: 'DE', 840: 'US',
  380: 'IT', 724: 'ES', 756: 'CH',
}

function numericToIso(id: number): string | null {
  return NUMERIC_TO_ISO[id] ?? null
}

function isScannedCountry(id: number): boolean {
  return !!NUMERIC_TO_ISO[id]
}

/**
 * Couleur d'un pays sur la carte.
 *
 * Échelle séquentielle monochrome, du clair au foncé selon le nombre de
 * détections. Un dégradé vert → orange → rouge encoderait une quantité par une
 * teinte, ce qui se lit mal et exclut les daltoniens ; et « vert » pour deux
 * marchands non déclarés serait un contresens.
 *
 * Les valeurs sont littérales : D3 pose `fill` comme attribut de présentation
 * SVG, qui ne résout pas les variables CSS.
 */
function getCountryFill(
  id: number,
  counts: Record<string, number>,
  isDark: boolean,
): string {
  const code = numericToIso(id)
  // Hors périmètre : ni scanné, ni surveillé.
  if (!code) return isDark ? 'rgba(255,255,255,.04)' : '#F1F5F9'

  const n = counts[code] ?? 0
  if (n === 0) return isDark ? 'rgba(255,255,255,.06)' : '#E2E8F0'
  if (n >= 6) return isDark ? '#B91C1C' : '#991B1B'
  if (n >= 3) return isDark ? '#7F1D1D' : '#DC2626'
  return isDark ? '#5B1616' : '#FCA5A5'
}
